import { get } from 'lodash-es'
import { parsePrimaryKeyAndSource, compareValuesNullsLast } from '@/components/shared-table/utils'
import { formatScientific } from '@/utils/formatters'
import {
  buildColumnDef,
  getFormattedValue,
  getModelPredictionRoot,
  getChemicalFromExtras,
} from '@/components/shared-table/buildColumnDef'
import { getProbabilityColor } from '@/components/combined-predict/models/utils/displayUtils'
import { getStandardizationChange } from '@/utils/chemical'

const getDisplayValue = (predictionContext) => {
  if (!predictionContext) return null

  const { predictionForProperty, sourceType } = predictionContext

  if (sourceType === 'positive' || sourceType === 'negative') {
    if (typeof predictionForProperty !== 'number') return ''
    return predictionForProperty >= 0.5 ? 'Yes' : 'No'
  }

  return formatScientific(predictionForProperty, { blank: '' })
}

const findPredictionContextByFieldKey = ({ modelId, predictionsByModelId, fieldKey, extrasByModelId, chemical }) => {
  const predictionForModel = getModelPredictionRoot(predictionsByModelId, modelId)
  if (!predictionForModel) return null

  const { sourceType } = parsePrimaryKeyAndSource(fieldKey, modelId)

  const { standardizedChemical } = getChemicalFromExtras(extrasByModelId, modelId)

  return {
    sourceType,
    predictionForProperty: get(predictionForModel, sourceType, null),
    confidence: get(predictionForModel, `${sourceType}_proba`, null),
    standardizationChange: getStandardizationChange(standardizedChemical, chemical),
  }
}

const buildAmenabilityColumnDef = ({ column, visibleColumnIds, tableSettings }) => {
  const isNotProbaField = (field) => !!field && typeof field === 'string' && !field?.includes('proba')
  return buildColumnDef({
    column,
    visibleColumnIds,
    tableSettings,

    // cellClass: (params) => {
    //   const value = getDisplayValue(params?.value, params?.colDef?.field)
    //   // if (value === 'Yes') return ['ag-right-aligned-cell', 'text-success']
    //   // if (value === 'No') return ['ag-right-aligned-cell', 'text-danger']
    //   return ['ag-right-aligned-cell']
    // },

    tooltipValueGetter: (params) => getDisplayValue(params?.value),

    ...(isNotProbaField(column?.id)
      ? {
          tooltipComponent: 'AmenabilityTooltip',

          tooltipComponentParams: (params) => {
            const predictionContext = findPredictionContextByFieldKey({
              modelId: column?.modelId,
              predictionsByModelId: params?.data?.predictionsByModelId,
              extrasByModelId: params?.data?.extrasByModelId,
              fieldKey: params?.colDef?.field,
              chemical: params?.data?.chemical,
            })
            return predictionContext ? { ...predictionContext } : null
          },

          cellClassRules: {
            'replaced-chemical-cell': (params) => {
              const rawValue = getDisplayValue(params?.value)
              return !!rawValue && !!params?.value?.standardizationChange
            },
          },
        }
      : {}),

    valueGetter: (params) =>
      findPredictionContextByFieldKey({
        modelId: column?.modelId,
        predictionsByModelId: params?.data?.predictionsByModelId,
        fieldKey: params?.colDef?.field,
        extrasByModelId: params?.data?.extrasByModelId,
        chemical: params?.data?.chemical,
      }),

    valueFormatter: (params) =>
      getFormattedValue({
        params,
        column,
        formatFunction: () => getDisplayValue(params?.value),
      }),

    cellStyle: (params) => {
      if (params?.colDef?.field.includes('proba')) {
        const raw = params?.value?.predictionForProperty

        if (!Number.isFinite(raw)) return
        if (raw < 0 || raw > 1) return
        return {
          backgroundColor: getProbabilityColor(raw),
        }
      }
    },

    comparator: (aValue, bValue, _nodeA, _nodeB, isDescending) => {
      const formattedAValue = getDisplayValue(aValue)
      const formattedBValue = getDisplayValue(bValue)
      return compareValuesNullsLast(formattedAValue, formattedBValue, isDescending)
    },
  })
}

export { buildAmenabilityColumnDef }
